import {
  action,
  flow,
  get,
  has,
  makeAutoObservable,
  observable,
  remove,
  set,
} from "mobx";

import WatsonApi from "../backends/WatsonApi";
import { convertFlatToTree } from "../utils/DataUtilities";
import { isJson } from "../components/admin/spinup/utilities";

export function mergeBlockItems(oldItems, newItems) {
  return [...oldItems, ...newItems].reduce((acc, current) => {
    if (!acc.find((c) => c.id === current.id)) {
      acc.push(current);
    }
    return acc;
  }, []);
}

export const blockLookups = {
  news: ["is_news"],
  events: ["is_event"],
  media: ["is_media"],
  files: ["is_file", "is_folder"],
};

export const enum fetchState {
  "PENDING SCHEMA",
  "PENDING RESPONSE",
  "SUCCESS",
  "FAILURE",
}

export class FetchState {
  public value;
  public message;

  constructor({ value, message }: { value: fetchState; message: string }) {
    this.value = value;
    this.message = message;
    makeAutoObservable(this);
  }
  set({ value, message }: { value: fetchState; message: string }) {
    this.value = value;
    this.message = message;
  }
}

export type IOrganizationStoreInitData = {
  organization: SoprisAppsProps["pageProps"]["organization"];
  currentOrganization: SoprisAppsProps["pageProps"]["currentOrganization"];
  user: SoprisAppsProps["pageProps"]["user"];
};

export default class OrganizationStore {
  private userId = null;
  initialized = false;
  initData: IOrganizationStoreInitData | null = null;

  constructor(initData: IOrganizationStoreInitData | undefined) {
    this.initialize(initData);

    makeAutoObservable(this, {
      updateThemeSettings: action,
      imageUpload: flow,
    });
  }

  initialize = (initData: IOrganizationStoreInitData | undefined) => {
    if (initData) {
      this.initData = {
        user: initData.user,
        organization: initData.organization,
        currentOrganization: initData.currentOrganization,
      };

      if (initData.organization) {
        this.organization = initData.organization;

        if (initData.organization.navigation) {
          this.navigation = initData.organization.navigation;
        }
      }

      if (initData.user) {
        this.setUserId(initData.user.id);
      }

      this.currentOrganization = initData.currentOrganization;
      this.initialized = true;
    }
  };

  serialize: () => IOrganizationStoreInitData = () => {
    return {
      organization: this
        .organization as IOrganizationStoreInitData["organization"],
      currentOrganization: this
        .currentOrganization as IOrganizationStoreInitData["currentOrganization"],
      user: (this.initData as IOrganizationStoreInitData).user,
    };
  };

  setUserId = (id) => {
    this.userId = id;
  };

  organization:
    | OrganizationTypeOrganization
    | Partial<OrganizationTypeOrganization> = {}; // title organization
  currentOrganization:
    | OrganizationTypeOrganization
    | Partial<OrganizationTypeOrganization> = {};
  _navigation: Navigation[] = [];

  content_sources: ContentSource[] = [];
  hostname = null;

  get userHasRole() {
    return (
      this.organization.user_roles?.is_staff ||
      this.organization.user_roles?.is_student ||
      this.organization.user_roles?.is_guardian
    );
  }

  get navigation() {
    return this._navigation;
  }

  set navigation(flatArray) {
    this._navigation = flatArray;
  }

  get navigationTree() {
    return convertFlatToTree(this._navigation);
  }

  get isMyCleverFeed() {
    return (
      this.currentOrganization.id === "ce7a0f60-a5fd-4740-b032-94a8910e67ba" ||
      this.currentOrganization.id === "d871cb40-74d3-4962-aae1-5ea13d0ce471"
    );
  }

  get schoolFeedEnabledForOrganization() {
    if (process.env.NEXT_PUBLIC_APP_CTX === "schoolblocks") {
      return !!this.organization?.schoolfeed_hostname;
    } else {
      return process.env.NEXT_PUBLIC_APP_CTX === "schoolfeed";
    }
  }

  setCurrentOrganization = (org: OrganizationTypeOrganization) => {
    this.currentOrganization = org;
  };

  updateThemeSettings = (obj, newThemeID = false) => {
    if (newThemeID && this.organization.json_data) {
      this.organization.json_data.settings.appearance.themeID =
        String(newThemeID);
    }
    const themeID = this.organization.json_data?.settings.appearance.themeID;
    if (themeID) {
      for (const k in obj) {
        if (obj.hasOwnProperty(k) && this.organization.json_data) {
          this.organization.json_data.settings.appearance.themes[themeID][k] =
            obj[k];
        }
      }
    }
  };

  fetchStates = observable.map();

  removeFetchState(requestId) {
    if (requestId) {
      remove(this.fetchStates, requestId);
    }
  }

  updateFetchState(requestId, state) {
    if (requestId) {
      if (has(this.fetchStates, requestId)) {
        get(this.fetchStates, requestId).set(state);
      } else {
        set(this.fetchStates, requestId, new FetchState(state));
      }
    }
  }

  async fetchClient(
    tag,
    fn,
    args = {},
    options: {
      requestId?: string;
      requestSuccessMessage?: string;
      organization_id?: OrganizationTypeOrganization["id"];
      user_id?: string;
      spinup?: boolean;
    } = {},
  ) {
    const {
      requestId = null, // ID for tracking progress of request
      requestSuccessMessage = "Beep, boop... all done, your site has been updated!",
    } = options;

    try {
      this.updateFetchState(requestId, { value: fetchState["PENDING SCHEMA"] });
      const client = await WatsonApi();
      this.updateFetchState(requestId, {
        value: fetchState["PENDING RESPONSE"],
      });
      const res = await client.apis[tag][fn](args);

      this.updateFetchState(requestId, {
        value: fetchState.SUCCESS,
        message: requestSuccessMessage,
      });
      return res;
    } catch (error) {
      let message = "";
      if (typeof error === "string") {
        message = `Oops! ${error}`;
      } else if (isJson(error)) {
        message = `Oops! ${JSON.parse(error).statusText.detail}`;
      } else if (isJson(error?.data)) {
        message = JSON.parse(error.data).detail;
      }
      this.updateFetchState(requestId, { value: fetchState.FAILURE, message });
    } finally {
      this.removeFetchState(requestId);
    }
  }

  buildOrganization(newOrg: OrganizationTypeOrganization) {
    let titleOrg = newOrg;

    if (titleOrg.categories && titleOrg.categories.length > 0) {
      newOrg.vertical = titleOrg.categories[0].path;
    } else {
      newOrg.vertical = "generic";
    }

    if (titleOrg.navigation && Array.isArray(titleOrg.navigation)) {
      // this map can be removed once we're totally on Watson, but for now there are some discrepancies between
      // the model field names of Yii and Watson, so this is here to keep things consistent
      this.navigation = titleOrg.navigation;
    }

    // hero should be an array, let's make it backwards compatible in case any objects or strings still exist
    if (
      titleOrg.json_data.settings.hero &&
      !Array.isArray(titleOrg.json_data.settings.hero)
    ) {
      titleOrg.json_data.settings.hero = [titleOrg.json_data.settings.hero];
    }

    this.organization = titleOrg;
  }

  updateSettings = function* (update, showNotification = true) {
    try {
      const result = yield this.fetchClient(
        "settings",
        "settings_update",
        {
          id: this.organization.id,
          data: { ...update },
        },
        {
          requestId: showNotification ? "updateSettings" : null,
        },
      );
      if (result?.data) {
        const settings = {
          ...this.organization.json_data.settings,
          ...JSON.parse(result.data).settings,
        };
        this.organization.json_data.settings = settings;
        return settings;
      }
    } catch (error) {
      console.error(error);
    }
  };

  imageUpload = function* (
    attribute: string,
    file: any,
    imageName:
      | "logo"
      | "hero"
      | "picture"
      | "icon"
      | "favicon"
      | "backgroundImage",
    organization_id?: number,
  ) {
    const client = yield WatsonApi();
    const targetOrgId = organization_id || this.currentOrganization.id;
    const obj =
      yield client.apis.organizations.organizations_file_upload_update({
        organization_pk: targetOrgId,
        name: imageName,
        file: file,
        mime_type: file.type,
        attribute,
      });
    if (obj?.data) {
      const media = JSON.parse(obj.data);

      if (attribute === "hero") {
        const currentHero = this.organization.json_data.settings.hero || [];
        const newHero = [media, ...currentHero];

        this.organization.json_data.settings = {
          ...this.organization.json_data.settings,
          hero: newHero,
        };
      } else if (attribute === "picture" && media.media_url) {
        // Only auto-update stores if updating the current/title organization
        if (targetOrgId === this.organization.id) {
          this.organization.picture = media.media_url;
        }
        if (targetOrgId === this.currentOrganization.id) {
          this.currentOrganization.picture = media.media_url;
        }
      }

      return media;
    }
  };

  pollSource = async (sourceId, organizationId) => {
    try {
      const obj = await this.fetchClient(
        "organizations",
        "organizations_content_sources_states_list",
        {
          contentsource_pk: sourceId,
          organization_pk: organizationId,
        },
      );
      if (obj?.data) {
        return JSON.parse(obj.data);
      }
    } catch (error) {
      console.error(error);
    }
  };
}
